import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { AgentService } from '@/services/agent.service'
import {
  IconFileUpload,
  IconLoader2,
  IconMessageQuestion,
  IconPlus,
  IconTrash,
  IconWorld
} from '@tabler/icons-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAgentConfig } from '../../context/agent-config-context'

export function BrainTab() {
  const { config, updateConfig, agentId } = useAgentConfig()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)

  const handleDeleteFile = async () => {
    if (!fileToDelete) return

    try {
      await AgentService.deleteFile(fileToDelete)

      updateConfig({
        files: config.files?.filter((f) => f.id !== fileToDelete),
      })

      toast.success('Arquivo excluído com sucesso')
    } catch (error) {
      console.error(error)
      toast.error('Erro ao excluir arquivo')
    } finally {
      setFileToDelete(null)
    }
  }

  // File Upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const file = files[0]

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/csv',
      'text/markdown',
    ]

    if (!allowedTypes.includes(file.type)) {
      toast.error(
        'Tipo de arquivo não permitido. Use PDF, TXT, DOCX, CSV ou MD.'
      )
      return
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 10MB')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Simulate progress (since we don't have real progress from axios)
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90))
      }, 200)

      if (!agentId) {
        toast.error('Salve o agente antes de fazer upload de arquivos')
        return
      }

      const uploadedFile = await AgentService.uploadFile(agentId, file)

      clearInterval(progressInterval)
      setUploadProgress(100)

      // Add file to config
      updateConfig({
        files: [...(config.files || []), uploadedFile],
      })

      toast.success('Arquivo enviado com sucesso!')

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    } catch (error) {
      toast.error('Erro ao fazer upload do arquivo')
      console.error(error)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      // Create a fake event to reuse the same handler
      const fakeEvent = {
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>
      handleFileSelect(fakeEvent)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  // Websites
  const [websiteInput, setWebsiteInput] = useState('')
  const addWebsite = () => {
    if (!websiteInput) return
    try {
      new URL(websiteInput)
      updateConfig({
        websites: [...(config.websites || []), websiteInput],
      })
      setWebsiteInput('')
      toast.success('Website adicionado')
    } catch {
      toast.error('URL inválida')
    }
  }

  const removeWebsite = (index: number) => {
    updateConfig({
      websites: config.websites?.filter((_, i) => i !== index),
    })
  }

  // YouTube
  const [youtubeInput, setYoutubeInput] = useState('')
  const addYoutube = () => {
    if (!youtubeInput) return
    try {
      new URL(youtubeInput)
      updateConfig({
        youtubeLinks: [...(config.youtubeLinks || []), youtubeInput],
      })
      setYoutubeInput('')
      toast.success('Link do YouTube adicionado')
    } catch {
      toast.error('URL inválida')
    }
  }

  const removeYoutube = (index: number) => {
    updateConfig({
      youtubeLinks: config.youtubeLinks?.filter((_, i) => i !== index),
    })
  }

  // Q&A
  const [qaPair, setQaPair] = useState({ question: '', answer: '' })
  const addQaPair = () => {
    if (!qaPair.question || !qaPair.answer) {
      toast.error('Preencha tanto a pergunta quanto a resposta')
      return
    }
    updateConfig({
      qaPairs: [...(config.qaPairs || []), qaPair],
    })
    setQaPair({ question: '', answer: '' })
    toast.success('Par de Q&A adicionado')
  }

  const removeQaPair = (index: number) => {
    updateConfig({
      qaPairs: config.qaPairs?.filter((_, i) => i !== index),
    })
  }

  return (
    <ScrollArea className='h-[calc(100vh-16rem)] pr-4'>
      <div className='space-y-6'>
        <div>
          <h3 className='text-lg font-medium'>Base de Conhecimento</h3>
          <p className='text-sm text-muted-foreground'>
            Adicione conteúdo para treinar seu agente e expandir seu
            conhecimento.
          </p>
        </div>

        <Tabs defaultValue='files' className='w-full'>
          <TabsList className='grid w-full grid-cols-3'>
            <TabsTrigger value='files'>
              <IconFileUpload className='mr-2 h-4 w-4' />
              Arquivos
            </TabsTrigger>
            <TabsTrigger value='websites'>
              <IconWorld className='mr-2 h-4 w-4' />
              Websites
            </TabsTrigger>
            {/* <TabsTrigger value='youtube'>
              <IconBrandYoutube className='mr-2 h-4 w-4' />
              YouTube
            </TabsTrigger> */}
            <TabsTrigger value='qa'>
              <IconMessageQuestion className='mr-2 h-4 w-4' />
              Q&A
            </TabsTrigger>
          </TabsList>

          {/* Arquivos */}
          <TabsContent value='files' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>Upload de Arquivos</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <input
                  ref={fileInputRef}
                  type='file'
                  className='hidden'
                  accept='.pdf,.txt,.docx,.csv,.md'
                  onChange={handleFileSelect}
                  disabled={isUploading}
                />
                <div
                  className='flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors hover:border-muted-foreground/50'
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                >
                  <div className='text-center'>
                    {isUploading ? (
                      <>
                        <IconLoader2 className='mx-auto mb-4 h-12 w-12 animate-spin text-muted-foreground' />
                        <p className='mb-2 text-sm text-muted-foreground'>
                          Enviando arquivo...
                        </p>
                        <Progress
                          value={uploadProgress}
                          className='mx-auto w-48'
                        />
                      </>
                    ) : (
                      <>
                        <IconFileUpload className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
                        <p className='text-sm text-muted-foreground'>
                          Clique ou arraste arquivos para fazer upload
                        </p>
                        <p className='mt-1 text-xs text-muted-foreground'>
                          Suporta: PDF, TXT, DOCX, CSV, MD (máx. 10MB)
                        </p>
                      </>
                    )}
                  </div>
                </div>

                {config.files && config.files.length > 0 && (
                  <div className='space-y-2'>
                    <Label>Arquivos Carregados</Label>
                    {config.files.map((file, index) => (
                      <div
                        key={file.id}
                        className='flex items-center justify-between rounded-lg border p-3'
                      >
                        <div className='flex-1'>
                          <p className='text-sm font-medium'>{file.name}</p>
                          <p className='text-xs text-muted-foreground'>
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => setFileToDelete(file.id)}
                        >
                          <IconTrash className='h-4 w-4 text-destructive' />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Websites */}
          <TabsContent value='websites' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>URLs de Websites</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex gap-2'>
                  <Input
                    placeholder='https://exemplo.com.br'
                    value={websiteInput}
                    onChange={(e) => setWebsiteInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addWebsite()}
                  />
                  <Button type='button' onClick={addWebsite}>
                    <IconPlus className='mr-2 h-4 w-4' />
                    Adicionar
                  </Button>
                </div>

                {config.websites && config.websites.length > 0 ? (
                  <div className='space-y-2'>
                    <Label>
                      {config.websites.length} website(s) adicionado(s)
                    </Label>
                    {config.websites.map((url, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between rounded-lg border p-3'
                      >
                        <p className='flex-1 truncate text-sm'>{url}</p>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => removeWebsite(index)}
                        >
                          <IconTrash className='h-4 w-4 text-destructive' />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='py-8 text-center text-sm text-muted-foreground'>
                    Nenhum website adicionado ainda.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* YouTube */}
          {/* <TabsContent value='youtube' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>Links do YouTube</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='flex gap-2'>
                  <Input
                    placeholder='https://youtube.com/watch?v=...'
                    value={youtubeInput}
                    onChange={(e) => setYoutubeInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addYoutube()}
                  />
                  <Button type='button' onClick={addYoutube}>
                    <IconPlus className='mr-2 h-4 w-4' />
                    Adicionar
                  </Button>
                </div>

                {config.youtubeLinks && config.youtubeLinks.length > 0 ? (
                  <div className='space-y-2'>
                    <Label>
                      {config.youtubeLinks.length} vídeo(s) adicionado(s)
                    </Label>
                    {config.youtubeLinks.map((url, index) => (
                      <div
                        key={index}
                        className='flex items-center justify-between rounded-lg border p-3'
                      >
                        <p className='flex-1 truncate text-sm'>{url}</p>
                        <Button
                          variant='ghost'
                          size='icon'
                          onClick={() => removeYoutube(index)}
                        >
                          <IconTrash className='h-4 w-4 text-destructive' />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='py-8 text-center text-sm text-muted-foreground'>
                    Nenhum vídeo adicionado ainda.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent> */}

          {/* Q&A */}
          <TabsContent value='qa' className='space-y-4'>
            <Card>
              <CardHeader>
                <CardTitle>Perguntas e Respostas</CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <div className='space-y-3'>
                  <div className='space-y-2'>
                    <Label>Pergunta</Label>
                    <Input
                      placeholder='Ex: Qual o horário de funcionamento?'
                      value={qaPair.question}
                      onChange={(e) =>
                        setQaPair({ ...qaPair, question: e.target.value })
                      }
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>Resposta</Label>
                    <Textarea
                      placeholder='Ex: Funcionamos de segunda a sexta, das 9h às 18h.'
                      value={qaPair.answer}
                      onChange={(e) =>
                        setQaPair({ ...qaPair, answer: e.target.value })
                      }
                      rows={3}
                    />
                  </div>
                  <Button type='button' onClick={addQaPair} className='w-full'>
                    <IconPlus className='mr-2 h-4 w-4' />
                    Adicionar Par Q&A
                  </Button>
                </div>

                <Separator />

                {config.qaPairs && config.qaPairs.length > 0 ? (
                  <div className='space-y-3'>
                    <Label>{config.qaPairs.length} par(es) adicionado(s)</Label>
                    {config.qaPairs.map((pair, index) => (
                      <div
                        key={index}
                        className='space-y-2 rounded-lg border p-3'
                      >
                        <div className='flex items-start justify-between'>
                          <div className='flex-1 space-y-1'>
                            <p className='text-sm font-medium'>
                              P: {pair.question}
                            </p>
                            <p className='text-sm text-muted-foreground'>
                              R: {pair.answer}
                            </p>
                          </div>
                          <Button
                            variant='ghost'
                            size='icon'
                            onClick={() => removeQaPair(index)}
                          >
                            <IconTrash className='h-4 w-4 text-destructive' />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='py-8 text-center text-sm text-muted-foreground'>
                    Nenhum par de Q&A adicionado ainda.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog
        open={!!fileToDelete}
        onOpenChange={(open) => !open && setFileToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O arquivo será permanentemente
              excluído do servidor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFile}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ScrollArea>
  )
}
